import { Injectable } from '@angular/core';
import { MenuItem, MessageService } from 'primeng/api';
import { ScannerQRCodeResult } from 'ngx-scanner-qrcode';
import { Product } from '../../../modals/product.modals';

/**
 * Shared dependency bag for the barcode/QR scanning cluster (barcode input
 * handlers, QR scan handling, scanner-delete-mode opening). onQRScan calls
 * toggleQR as a sibling (synchronous, no async gap, so reusing the same deps
 * snapshot is safe). `getBarcodeInput` is a LIVE getter (not a snapshot):
 * onBarcodeInput's 150ms debounce timer must re-read the current value when
 * it fires, not the value captured at the keystroke that scheduled it --
 * otherwise a blur/enter that processes+clears the field just before the
 * timer fires would be invisible to the timer, causing a duplicate add.
 */
export interface BarcodeScannerDeps {
  barcodeInput: string;
  getBarcodeInput: () => string;
  barcodeInputTimer: any;
  isDeleteMode: boolean;
  enableCursorFocus: boolean;
  isExchangeOrder: boolean;
  products: Product[];
  hasScannerClicked: boolean;
  items: MenuItem[] | undefined;

  setBarcodeInput: (value: string) => void;
  setBarcodeInputTimer: (value: any) => void;
  setHasScannerClicked: (value: boolean) => void;
  setShowItemDetail: (value: boolean) => void;
  setDisplayDialog: (value: boolean) => void;
  setDisplayTransactions: (value: boolean) => void;
  setActiveItem: (value: any) => void;
  setDisplayNoItem: (value: boolean) => void;
  setShowBillDetail: (value: boolean) => void;

  removeItemByPSID: (psid: string) => void;
  addProducts: (psid: string) => void;
  focusQuantityInput: () => void;
  scheduleOpenScanner: () => void;
}

/**
 * BarcodeScannerService
 *
 * Dedicated service for barcode-gun input handling (Enter/blur/rapid-input
 * debounce), QR scan result handling, and opening the scanner in delete mode.
 *
 * Extracted from DashboardComponent to decouple scanning logic while
 * preserving 100% identical behavior.
 */
@Injectable({
  providedIn: 'root',
})
export class BarcodeScannerService {
  constructor(private readonly messageService: MessageService) {}

  onBarcodeEnter(deps: BarcodeScannerDeps) {
    if (deps.barcodeInput && deps.barcodeInput.trim()) {
      const barcode = deps.barcodeInput.trim();
      console.log('barcode enter');
      if (deps.isDeleteMode) {
        // In Delete Mode: Remove item instead of adding
        deps.removeItemByPSID(barcode);
      } else {
        // Normal Mode: Add item
        deps.addProducts(barcode);
        if (deps.enableCursorFocus) {
          setTimeout(() => {
            deps.focusQuantityInput();
          }, 250);
        }
      }

      deps.setBarcodeInput('');
    }
  }

  onBarcodeInput(deps: BarcodeScannerDeps) {
    if (deps.barcodeInputTimer) {
      clearTimeout(deps.barcodeInputTimer);
    }

    // Assuming gun scanners typically finish within 100-200ms
    deps.setBarcodeInputTimer(
      setTimeout(() => {
        const currentBarcodeInput = deps.getBarcodeInput();
        if (currentBarcodeInput && currentBarcodeInput.trim().length >= 50) {
          const barcode = currentBarcodeInput.trim();

          if (deps.isDeleteMode) {
            // In Delete Mode: Remove item from cart
            deps.removeItemByPSID(barcode);
          } else {
            // Normal Mode: Add item to cart
            deps.addProducts(barcode);
            if (deps.enableCursorFocus) {
              setTimeout(() => {
                deps.focusQuantityInput();
              }, 100);
            }
          }

          deps.setBarcodeInput('');
        }
      }, 150),
    );
  }

  onBarcodeBlur(deps: BarcodeScannerDeps) {
    if (deps.barcodeInput && deps.barcodeInput.trim()) {
      const barcode = deps.barcodeInput.trim();

      if (deps.isDeleteMode) {
        deps.removeItemByPSID(barcode);
      } else {
        deps.addProducts(barcode);
        if (deps.enableCursorFocus) {
          setTimeout(() => {
            deps.focusQuantityInput();
          }, 100);
        }
      }

      deps.setBarcodeInput('');
    }
  }

  onQRScan(
    itemScanned: ScannerQRCodeResult[],
    action: any,
    deps: BarcodeScannerDeps,
  ): void {
    if (itemScanned) {
      deps.setShowItemDetail(false); // hide item detail tab
      deps.setDisplayDialog(false); // hide transaction detail dialog
      deps.setDisplayTransactions(false); // hide transaction table as well

      if (deps.isDeleteMode) {
        // In Delete Mode: Remove item from cart
        deps.removeItemByPSID(itemScanned[0].value);
      } else {
        // Normal Mode: Add item to cart
        deps.addProducts(itemScanned[0].value);
        if (deps.enableCursorFocus) {
          setTimeout(() => {
            deps.focusQuantityInput();
          }, 100);
        }
      }

      deps.setActiveItem(deps.items && deps.items[0]);
      deps.setDisplayNoItem(false);
      deps.setShowBillDetail(true);

      // Don't reopen it automatically - user must click barcode icon to open again
      if (deps.hasScannerClicked) {
        this.toggleQR(deps);
      }
    }
  }

  toggleQR(deps: BarcodeScannerDeps) {
    deps.setHasScannerClicked(!deps.hasScannerClicked);
    deps.scheduleOpenScanner();
  }

  openScannerForDelete(deps: BarcodeScannerDeps) {
    // Only allow scanner for delete in place order mode (not exchange mode)
    if (deps.isExchangeOrder) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Not Available',
        detail: 'Scanner delete is not available in exchange mode',
        life: 3000,
      });
      return;
    }

    // Check if there are items to delete
    if (!deps.products || deps.products.length === 0) {
      this.messageService.add({
        severity: 'info',
        summary: 'No Items',
        detail: 'No items in cart to delete',
        life: 3000,
      });
      return;
    }

    // Open scanner (Delete Mode is already active via isDeleteMode flag)
    deps.setHasScannerClicked(true);

    this.messageService.add({
      severity: 'info',
      summary: 'Delete Mode',
      detail: 'Scanner opened in delete mode. Scan item to remove from cart.',
      life: 3000,
    });

    deps.scheduleOpenScanner();
  }

  findScannedProduct(
    psid: string,
    sellingprice: number | undefined,
    batch: string | undefined,
    isBatchEnabled: boolean,
    productsTemp: Partial<Product[]>,
  ): Product | null {
    // Case-insensitive comparison for barcode/PSID to prevent duplicate items
    // When isBatchEnabled is false: unique identifier is psid + sellingprice
    // When isBatchEnabled is true: unique identifier is psid + batch

    if (isBatchEnabled) {
      // Batch mode: match psid + batch
      return (
        productsTemp.find(
          (detail: any) =>
            detail.psid?.toLowerCase() === psid?.toLowerCase() &&
            detail.batch === batch,
        ) || null
      );
    } else {
      // Non-batch mode: match psid + sellingprice
      return (
        productsTemp.find(
          (detail: any) =>
            detail.psid?.toLowerCase() === psid?.toLowerCase() &&
            detail.sellingprice === sellingprice,
        ) || null
      );
    }
  }
}
